import { Output } from "valibot";

import {
  BaseIntegrationsSchema,
  BotInfoSchema,
  ChatHistory,
} from "components/SonicBot/schemas";

import { generateIntegrationHandlers } from "./integrations/utils";
import { requestBackend } from "./request";
import { BotTrainedStatus, GetBotDataTrainedStatus } from "./types";

export enum WidgetPosition {
  left = "left",
  right = "right",
}

export enum ResponseLength {
  short = "short",
  medium = "medium",
  long = "long",
}

export interface BotPromptConfig {
  bot_id: string;
  generation_language: string;
  is_conversational: boolean;
}
export interface BotConfig {
  id: string;
  bot_info_config: Output<typeof BotInfoSchema>;
  bot_prompt_config: BotPromptConfig;
  vectorstore_index_id: string;
  api_token: string;
  system_prompt: string;
  updated_at: string;
  is_shared: boolean;
  user_form_visibility?: boolean;
}

// chat user form data
export interface ChatUserInputValue {
  key: string;
  value: string;
  id: string;
}

export interface ChatUserFormData {
  _id: string;
  created_at: string;
  data_values: ChatUserInputValue[];
}

export interface ChatGeneration {
  answer: string;
  chat_history: any;
}

export interface ChatInput {
  question: any;
  chat_history: any;
  token: string;
  service_base_URL: string;
  botId: string;
}

export interface GetBotSonicIntegrationsInput {
  botId: string;
}

export interface ActiveCampaignFields {
  id: string;
  title: string | null;
  type: string;
  perstag: string | null;
  owner: string;
  contact: string | null;
  field: string | null;
  value: string | null;
}

export interface ActiveCampaignUserData {
  email: string | null;
  phone: string | null;
  first_name: string | null;
  last_name: string | null;
  org_id: string | null;
  fields: ActiveCampaignFields[];
  welcome_message: string | null;
}

export const getBotSonicIntegrations = async ({
  botId,
}: GetBotSonicIntegrationsInput) =>
  requestBackend(
    `/v1/botsonic/${botId}/third-party-integrations?isActive=true`,
    {
      schema: BaseIntegrationsSchema,
    },
  )
    .then(data => {
      return generateIntegrationHandlers(data);
    })
    .catch();

export const saveBotSonic = ({
  token,
  botId,
  chat_id,
  chat_history,
}: {
  token: string;
  botId: string;
  chat_id: string;
  chat_history: any;
}): Promise<any> =>
  requestBackend(`/v1/botsonic/history/save`, {
    method: "POST",
    headers: {
      token,
    },
    body: JSON.stringify({
      bot_id: botId,
      chat_id,
      chat_history,
    }),
    // eslint-disable-next-line no-console
  }).catch(err => console.error(err));

export const createUser = ({
  token,
  data,
  chatId,
}: {
  baseURL: string;
  token: string;
  data: ChatUserInputValue[];
  chatId: string;
}): Promise<ChatUserFormData> =>
  requestBackend(`/v1/botsonic/widget/chat-user-form-data`, {
    method: "post",
    headers: {
      token,
    },
    body: JSON.stringify({
      data_values: data,
      chat_id: chatId,
    }),
  }) as any;

export const saveLikeDislike = ({
  token,
  chatId,
  messageId,
  likeStatus,
  isOverallChatFeedback = false,
  isResolved,
  additionalFeedback,
  chatEnded = false,
}: {
  token: string;
  chatId: string;
  messageId?: string;
  likeStatus?: string;
  isOverallChatFeedback?: boolean;
  isResolved?: boolean;
  additionalFeedback?: string;
  chatEnded?: boolean;
}): Promise<any> =>
  requestBackend(`/v1/botsonic/widget/like-dislike`, {
    method: "post",
    headers: {
      token,
    },
    body: JSON.stringify({
      chat_id: chatId,
      message_id: messageId,
      like_status: likeStatus,
      is_overall_chat_feedback: isOverallChatFeedback,
      is_resolved: isResolved,
      additional_feedback: additionalFeedback,
      chat_ended: chatEnded,
    }),
  });

export const requestCallback = ({
  token,
  chatUserFormDataId,
  chatId,
}: {
  token: string;
  chatUserFormDataId: string;
  chatId: string;
}): Promise<ChatUserFormData> =>
  requestBackend(`/v1/botsonic/widget/request-callback`, {
    method: "post",
    headers: {
      token,
    },
    body: JSON.stringify({
      chat_user_id: chatUserFormDataId,
      chat_history_id: chatId,
    }),
  }) as any;

export const getChatHistory = async ({
  token,
  chatId,
}: {
  token: string;
  chatId: string;
}): Promise<Output<typeof ChatHistory>> => {
  const response: any = await requestBackend(
    `/v1/botsonic/widget/chat-history?chat_id=${chatId}`,
    {
      headers: {
        token,
      },
    },
  );

  const mappedChatData = response?.chat_data?.map((chatData: any) => {
    return {
      id: chatData.id,
      message: chatData.message,
      sent: chatData.sent,
      sources: chatData.sources,
      follow_ups: chatData.follow_ups,
      showLikeDislikeBtn: chatData.is_via_live_agent ? false : true,
      integration: chatData?.integration,
      likeStatus: chatData.like_status,
      additionalInfo: {
        agentInfo: chatData.additional_info?.agent_info,
        chatMessageType: chatData.additional_info?.chat_message_type,
        createdTicketInfo: chatData.additional_info?.created_ticket_info,
      },
      user_form_info: chatData.user_form_info,
      submitted_form_field_data: chatData.submitted_form_field_data,
      is_intermediate: chatData.is_intermediate,
      renderableSource: chatData.renderable_sources?.length
        ? chatData.renderable_sources?.[0]
        : undefined,
    };
  });

  return {
    ...response,
    chat_data: mappedChatData,
  };
};

export const getActiveCampaignFields = async ({
  email,
  token,
}: {
  email: string;
  token: string;
}): Promise<ActiveCampaignUserData> =>
  requestBackend(
    `/v1/botsonic/widget/active-campaign/field-values?email=${email}`,
    {
      method: "get",
      headers: {
        token,
      },
    },
  )
    .then(data => {
      return data as any;
    })
    .catch();

export const upsertChatIdToActiveCampaign = async ({
  email,
  chatId,
  token,
}: {
  email: string;
  chatId: string;
  token: string;
}): Promise<any> =>
  requestBackend(`/v1/botsonic/widget/active-campaign/upsert-chat`, {
    method: "put",
    headers: {
      token,
    },
    body: JSON.stringify({
      email,
      chat_id: chatId,
    }),
  });

export const getSourcesTrainedStatus = async ({
  botId,
  token,
}: GetBotDataTrainedStatus) =>
  requestBackend(`/v1/botsonic/widget/${botId}/trained-status`, {
    headers: {
      token,
    },
  })
    .then(data => {
      return data as BotTrainedStatus;
    })
    .catch();

export const getPresignedUrl = async ({
  fileKey,
  mimetype,
  botId,
  validityHours,
}: {
  fileKey: string;
  mimetype: string;
  botId: string;
  validityHours: number;
}): Promise<any> =>
  requestBackend(
    `/v1/data-upload/presigned-url?file_key=${fileKey}&mimetype=${mimetype}&bot_id=${botId}&validity_hours=${validityHours}`,
    {
      method: "get",
    },
  );
